// src/components/GameDetailModalV2.js
import React, { useEffect } from 'react';
import { useGameDetails } from '../hooks/useGameDetails';
import { X, Users, Clock, Baby, BrainCircuit, Calendar, Sparkles } from 'lucide-react';

function TagList({ title, tags }) {
    const getTagName = (tag) => tag.nm_categoria || tag.NmCategoria || tag.nm_mecanica || tag.NmMecanica;
    if (!tags || tags.length === 0) return null;
    return (
        <div className="mt-8">
            <h3 className="font-serif text-sm tracking-[0.2em] text-[#528a89] uppercase mb-4 flex items-center gap-2">
                <Sparkles size={14}/> {title}
            </h3>
            <div className="flex flex-wrap gap-2">
                {tags.map((tag, index) => (
                    <span key={index} className="px-3 py-1 border border-[#dbcdac] text-[#971253] text-xs font-medium uppercase tracking-wider rounded-full hover:bg-[#dbcdac] hover:text-white transition-colors cursor-default">
                        {getTagName(tag)}
                    </span>
                ))}
            </div>
        </div>
    );
}

function GameDetailModalV2({ gameId, onClose }) {
    const { gameDetails, isLoading, error } = useGameDetails(gameId);

    // Lock scroll on mount
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'auto'; };
    }, []);

    const renderContent = () => {
        if (isLoading) return <div className="py-40 flex justify-center"><div className="w-12 h-12 border-4 border-[#dbcdac] border-t-[#971253] rounded-full animate-spin"></div></div>;
        if (error) return <div className="py-20 text-center font-serif text-xl text-[#971253]">{error}</div>;
        if (!gameDetails) return null;

        const game = {
            nm_jogo: gameDetails.nm_jogo || gameDetails.NomeJogo,
            thumb: gameDetails.thumb || gameDetails.Thumb,
            qt_jogadores_min: gameDetails.qt_jogadores_min || gameDetails.JogadoresMin,
            qt_jogadores_max: gameDetails.qt_jogadores_max || gameDetails.JogadoresMax,
            vl_tempo_jogo: gameDetails.vl_tempo_jogo || gameDetails.TempoJogo,
            ano_publicacao: gameDetails.ano_publicacao || gameDetails.AnoPublicacao,
            idade_minima: gameDetails.idade_minima || gameDetails.IdadeMinima,
            complexidade: gameDetails.complexidade,
            mecanicas: gameDetails.mecanicas || gameDetails.Mecanicas || [],
            categorias: gameDetails.categorias || gameDetails.Categorias || [],
        };

        return (
            <div className="flex flex-col md:flex-row gap-10 items-start">
                {/* Left: Image Box */}
                <div className="w-full md:w-2/5 shrink-0 bg-white/50 p-6 rounded-2xl border border-[#dbcdac]/40 shadow-inner flex items-center justify-center">
                    <img src={game.thumb || '/images/placeholder.webp'} alt={game.nm_jogo} className="max-w-full h-auto object-contain mix-blend-multiply drop-shadow-2xl" />
                </div>

                {/* Right: Info Sheet */}
                <div className="w-full md:w-3/5">
                    <h2 className="font-serif text-4xl md:text-5xl text-[#971253] leading-tight mb-2">{game.nm_jogo}</h2>
                    <div className="h-[2px] w-24 bg-[#dbcdac] mb-8" />

                    {/* Elegant Grid for Metadata */}
                    <div className="grid grid-cols-2 gap-y-6 gap-x-4 mb-8">
                        <div className="flex items-center gap-3 text-stone-700">
                            <div className="p-2 bg-[#528a89]/10 rounded-full text-[#528a89]"><Users size={20} /></div>
                            <div><p className="text-[10px] uppercase tracking-widest text-stone-400">Jogadores</p><p className="font-serif text-lg leading-none">{game.qt_jogadores_min}-{game.qt_jogadores_max}</p></div>
                        </div>
                        <div className="flex items-center gap-3 text-stone-700">
                            <div className="p-2 bg-[#971253]/10 rounded-full text-[#971253]"><Clock size={20} /></div>
                            <div><p className="text-[10px] uppercase tracking-widest text-stone-400">Duração</p><p className="font-serif text-lg leading-none">{game.vl_tempo_jogo} min</p></div>
                        </div>
                        <div className="flex items-center gap-3 text-stone-700">
                            <div className="p-2 bg-[#dbcdac]/30 rounded-full text-[#7a6f58]"><BrainCircuit size={20} /></div>
                            <div><p className="text-[10px] uppercase tracking-widest text-stone-400">Complexidade</p><p className="font-serif text-lg leading-none">{game.complexidade || 'N/A'}</p></div>
                        </div>
                        <div className="flex items-center gap-3 text-stone-700">
                            <div className="p-2 bg-stone-100 rounded-full text-stone-500"><Calendar size={20} /></div>
                            <div><p className="text-[10px] uppercase tracking-widest text-stone-400">Publicação</p><p className="font-serif text-lg leading-none">{game.ano_publicacao || 'N/A'}</p></div>
                        </div>
                    </div>

                    <div className="border-t border-dashed border-[#dbcdac]/60 pt-4">
                        <TagList title="Categorias" tags={game.categorias} />
                        <TagList title="Mecânicas" tags={game.mecanicas} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose}>
            {/* O Modal Vintage Sheet */}
            <div 
                className="bg-[#FFF5EE] w-full max-w-5xl max-h-[95vh] overflow-y-auto rounded-xl border-4 border-double border-[#dbcdac] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] relative p-8 md:p-12 animate-in zoom-in-95 duration-300"
                onClick={e => e.stopPropagation()}
            >
                {/* Close Button Decorativo */}
                <button onClick={onClose} className="absolute top-6 right-6 p-2 text-[#528a89] hover:text-[#971253] hover:bg-[#971253]/10 rounded-full transition-colors">
                    <X size={28} strokeWidth={1.5} />
                </button>
                
                {renderContent()}
            </div>
        </div>
    );
}

export default GameDetailModalV2;